/**
 * React Query hooks for the unified Script + Content review tabs.
 * Split shapes (RosterEntryScript / RosterEntryContent) match the
 * BE — Script rows never carry live_url, Content rows always do (or
 * omit it when admin hasn't pasted a URL yet).
 *
 * No KPI hook here — review tabs don't carry a tab-aggregated headline
 * number, per the locked plan.
 */
import { useQuery } from '@tanstack/react-query';

import type { RosterEntryContent, RosterEntryScript } from '@discovr/types';

import {
  createCampaignDetailApi,
  type UnifiedListParams,
} from '../api/campaignQueries';
import type { PagedResult } from '../api/shared';
import { campaignKeys } from '../queryKeys';

const api = createCampaignDetailApi();

/**
 * Paginated Scripts-tab list (status ∈ {script_pending, script_review}).
 * Activity-sorted server-side.
 */
export function useScriptTab(
  campaignId: string,
  params: UnifiedListParams = {},
) {
  return useQuery<PagedResult<RosterEntryScript>>({
    queryKey: campaignKeys.rosterTab(campaignId, 'scripts', params),
    queryFn: () => api.listScript(campaignId, params),
    enabled: !!campaignId,
    staleTime: 30_000,
    // SSE keeps this list live; disabling focus refetch keeps `isRefetching` a
    // clean "SSE-driven update" signal for the JumpToTopPill (no false pings on
    // window refocus).
    refetchOnWindowFocus: false,
  });
}

/**
 * Paginated Content-tab list (status ∈ {content_pending, content_review}).
 * Each row carries live_url for the content-version URL.
 */
export function useContentTab(
  campaignId: string,
  params: UnifiedListParams = {},
) {
  return useQuery<PagedResult<RosterEntryContent>>({
    queryKey: campaignKeys.rosterTab(campaignId, 'content', params),
    queryFn: () => api.listContent(campaignId, params),
    enabled: !!campaignId,
    staleTime: 30_000,
    // See useScriptTab: SSE keeps it live; focus refetch off → clean pill signal.
    refetchOnWindowFocus: false,
  });
}
